// src/modules/app-views/invoices/pages/Document/tabs/RecordTab/RecipientCard.jsx
// *******************************************************************************
// INVOICE RECIPIENT CARD
// *******************************************************************************
// Card for selecting and displaying recipient/recipient information.
// Includes recipient search, basic info display, and address details.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Row, Col, Form, Descriptions, Grid, Divider } from 'antd';
import SimpleSelectAsyncEntities from 'components/SimpleComponents/SimpleSelectAsyncEntities';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleCpf from 'components/SimpleComponents/SimpleCpf';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import { RECIPIENT_IE_INDICATOR } from 'services/fiscalService';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import { openModal } from 'components/core/GenericModal';
import EditEntityModal from 'modules/app-views/customers/components/EditEntityModal';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import Blob from 'components/core/Blob';
import { parseIeToDisplay, parsePhoneToDisplay } from 'utils/helpers';

// Module-level constant for contact person sub-select (stable reference)
const CONTACT_PROFILES = ['contact'];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const RecipientCard = ({ data, viewMode, onActionsChange, name = 'recipient', entityProfiles, label, contactName }) => {
  // -----------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // -----------------------------------------------------------------------------
  const { form } = useSimpleFormContext();

  // -----------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // -----------------------------------------------------------------------------
  // Responsive breakpoint detection
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  // Stable ref for entityProfiles - avoids recreating callbacks when parent passes inline array
  const entityProfilesRef = useRef(entityProfiles);
  useEffect(() => {
    entityProfilesRef.current = entityProfiles;
  }, [entityProfiles]);

  // Subscribe to language changes to re-render when language changes
  const storedLanguage = useZustand('theme', 'language');

  // Get generic person entity for NFCe auto-fill
  const storedGenericPerson = useZustand('auth', 'genericPerson');

  // -----------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // -----------------------------------------------------------------------------
  const isLocked = viewMode === 'view';
  const isAuthorized = data?.status === 'authorized';

  // Watch document type to show optional recipient for NFCe and required B2B fields for NFe
  const documentType = Form.useWatch('documentType', form);
  const isNfce = documentType === 'nfce';
  const isNfe = documentType === 'nfe';

  // Whether this card is in invoice-recipient mode (fiscal fields enabled)
  const isRecipient = name === 'recipient';
  // Dynamic data key names derived from the name prop
  const nameId = `${name}Id`;          // e.g., 'recipientId' or 'entityId'
  const nameEntity = `${name}Entity`;  // e.g., 'recipientEntity' or 'entityEntity'
  const nameDisplay = `${name}Display`; // e.g., 'recipientDisplay' or 'entityDisplay'

  // Contact sub-select (shown when company is selected and contactName is provided)
  const contactNameId = contactName ? `${contactName}Id` : null;       // e.g., 'personContactId'
  const contactNameEntity = contactName ? `${contactName}Entity` : null; // e.g., 'personContactEntity'

  // Watch IE indicator for NFe B2B requirements
  const indIEDest = Form.useWatch([name, 'indIEDest'], form);
  const isContributor = indIEDest === RECIPIENT_IE_INDICATOR.CONTRIBUTOR;

  // Get genericCompany entity
  const storedGenericCompany = useZustand('auth', 'genericCompany');

  // -----------------------------------------------------------------------------
  // 4. LOCAL STATE
  // -----------------------------------------------------------------------------
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  // Contact person selected within the company (only when contactName prop is provided)
  const [selectedContact, setSelectedContact] = useState(null);

  // Check if selected recipient is a generic entity (consumer without CPF/CNPJ)
  // Priority: selectedRecipient (user just selected) > data.recipientEntity (saved) > recipientDisplay
  const isGenericEntity = useMemo(() => {
    // Fallback to data snapshot for reversal/complementary invoices that may not
    // have display or entity hydrated by backend aggregate
    const recipient =
      selectedRecipient || data?.[nameEntity] || data?.[nameDisplay] || data?.[name];
    if (!recipient) return false;

    // Check entityWithFlags - prioritize selectedRecipient if user just selected something
    // Only use data entity if no selection was made (viewing saved document)
    const entityWithFlags = selectedRecipient || data?.[nameEntity] || recipient;

    // Check for explicit flags
    const hasGenericFlags =
      entityWithFlags?.finalConsumer ||
      entityWithFlags?.genericPerson ||
      entityWithFlags?.genericCompany;

    // Check if matches system generic entities
    const matchesSystemGeneric =
      (storedGenericPerson?._id && recipient._id === storedGenericPerson._id) ||
      (storedGenericCompany?._id && recipient._id === storedGenericCompany._id);

    // Check if entity has no document (uniqueKey is null/empty = no CPF/CNPJ)
    // This catches migrated "CONSUMIDOR" entities that don't have flags
    // Also check documents array (power-search returns CPF/CNPJ there)
    const hasUniqueKey = !!entityWithFlags?.uniqueKey;
    const hasDocumentInArray = entityWithFlags?.documents?.some(
      d => d.documentType === 'cpf' || d.documentType === 'cnpj'
    );
    const hasCpfOrCnpjDirect =
      !!entityWithFlags?.cpf?.value ||
      !!entityWithFlags?.cpf ||
      !!entityWithFlags?.cnpj?.value ||
      !!entityWithFlags?.cnpj;
    const hasNoDocument = !hasUniqueKey && !hasDocumentInArray && !hasCpfOrCnpjDirect;

    return hasGenericFlags || matchesSystemGeneric || hasNoDocument;
  }, [
    selectedRecipient,
    data?.[nameEntity],
    data?.[nameDisplay],
    data?.[name],
    storedGenericPerson?._id,
    storedGenericCompany?._id,
  ]);

  // Check if selected recipient is a SYSTEM generic entity (genericPerson/genericCompany)
  // These entities should NOT be editable (different from migrated consumers without documents)
  const isSystemGenericEntity = useMemo(() => {
    const recipient =
      selectedRecipient || data?.[nameEntity] || data?.[nameDisplay] || data?.[name];
    if (!recipient) return false;

    const entityWithFlags = selectedRecipient || data?.[nameEntity] || recipient;

    // Check for genericPerson or genericCompany flags (NOT finalConsumer)
    const hasSystemGenericFlags =
      entityWithFlags?.genericPerson || entityWithFlags?.genericCompany;

    // Check if matches system generic entities by ID
    const matchesSystemGeneric =
      (storedGenericPerson?._id && recipient._id === storedGenericPerson._id) ||
      (storedGenericCompany?._id && recipient._id === storedGenericCompany._id);

    return hasSystemGenericFlags || matchesSystemGeneric;
  }, [
    selectedRecipient,
    data?.[nameEntity],
    data?.[nameDisplay],
    data?.[name],
    storedGenericPerson?._id,
    storedGenericCompany?._id,
  ]);

  // -----------------------------------------------------------------------------
  // 5. EFFECTS
  // -----------------------------------------------------------------------------
  // Initialize selectedRecipient and form field from data when editing
  // entity is hydrated by backend aggregate pipeline
  useEffect(() => {
    let recipient = null;

    // Get entity ID from multiple possible locations
    const recipientId =
      data?.[nameId] || data?.[name]?.entityId || data?.[nameEntity]?._id;

    if (data?.[nameDisplay]) {
      recipient = data[nameDisplay];
    } else if (data?.[nameEntity]) {
      recipient = data[nameEntity];
    } else if (data?.[name] && (recipientId || data[name].name || data[name].fullName)) {
      // Fallback: use snapshot
      recipient = {
        ...(recipientId ? { _id: recipientId } : {}),
        ...data[name],
      };
    }

    if (recipient) {
      setSelectedRecipient(recipient);

      // Also set the ID field in the form so validation passes on save
      if (form && recipientId) {
        form.setFieldsValue({ [nameId]: recipientId });
      }

      // Normalize backend field names to frontend form field names (invoice-mode only).
      // When a reversal/complementary invoice is created by the backend, the snapshot
      // may use entity schema names instead of form field names.
      if (isRecipient && form && data?.[name]) {
        const snap = data[name];
        const normalized = normalizeRecipientSnapshot(snap);
        if (Object.keys(normalized).length > 0) {
          form.setFieldsValue({ [name]: normalized });
        }
      }
    }
  }, [
    data?.[nameDisplay],
    data?.[nameEntity],
    data?.[nameId],
    data?.[name],
    data?.[name]?.entityId,
    form,
    isRecipient,
  ]);

  // Auto-fill with generic person when NFCe and no recipient selected (invoice-mode only)
  useEffect(() => {
    if (!isRecipient || !isNfce || !storedGenericPerson || selectedRecipient) return;

    // Only auto-fill for new documents (no existing entity ID in data)
    if (data?.[nameId]) return;

    // Set generic person as selected recipient
    setSelectedRecipient(storedGenericPerson);

    // Set form values
    if (form) {
      form.setFieldsValue({
        [nameId]: storedGenericPerson._id,
        [name]: {
          entityId: storedGenericPerson._id,
          name: storedGenericPerson.name,
          fullName: storedGenericPerson.fullName,
        },
      });
    }
  }, [isRecipient, isNfce, storedGenericPerson, selectedRecipient, data?.[nameId], form]);

  // Initialize selectedContact from data when contactName prop is provided
  useEffect(() => {
    if (!contactName || !data) return;
    const contactEntity = data?.[contactNameEntity];
    const contactId = data?.[contactNameId];

    if (contactEntity) {
      setSelectedContact(contactEntity);
      if (form && contactId) {
        form.setFieldsValue({ [contactNameId]: contactId });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.[contactNameEntity], data?.[contactNameId], form, contactName]);

  // -----------------------------------------------------------------------------
  // 6. HANDLERS
  // -----------------------------------------------------------------------------

  // Handle entity selection (from search or modal save)
  const handleEntitySelect = useCallback(
    entity => {
      setSelectedRecipient(entity);

      // Clear contact person when entity changes (contact belongs to a specific company)
      if (contactNameId) {
        setSelectedContact(null);
        form?.setFieldsValue({ [contactNameId]: undefined });
      }

      if (entity && form) {
        // Extract phone value
        const phoneValue = getPhoneValue(entity);
        // Extract email value
        const emailValue = getEmailValue(entity);

        // Try multiple sources for CPF (power-search may return in documents array)
        let entityCpf = entity.cpf?.value || entity.cpf;
        if (!entityCpf && entity.documents?.length) {
          const cpfDoc = entity.documents.find(d => d.documentType === 'cpf');
          entityCpf = cpfDoc?.value;
        }

        // Try multiple sources for CNPJ
        let entityCnpj = entity.cnpj?.value || entity.cnpj;
        if (!entityCnpj && entity.documents?.length) {
          const cnpjDoc = entity.documents.find(d => d.documentType === 'cnpj');
          entityCnpj = cnpjDoc?.value;
        }

        // Determine if person: check entityType OR infer from documents
        // Power-search may not return entityType, so we infer: has CPF and no CNPJ = person
        const isPerson = entity.entityType === 'person' || (!!entityCpf && !entityCnpj);

        // Pre-fill cpfCnpj: CPF for persons, CNPJ for companies (required by NFe validator)
        const cpfCnpjValue = isPerson ? (entityCpf || undefined) : (entityCnpj || undefined);

        // Extract IE value - try multiple sources (power-search may return IE in documents array)
        let entityIe = entity.ie?.value || (typeof entity.ie === 'string' ? entity.ie : null);
        if (!entityIe && entity.documents?.length) {
          const ieDoc = entity.documents.find(d => d.documentType === 'ie');
          entityIe = ieDoc?.value || null;
        }
        if (!entityIe && entity.fiscalData?.stateRegistration) {
          entityIe = entity.fiscalData.stateRegistration;
        }

        // Extract SUFRAMA value
        const entitySuframa =
          entity.inscricaoSuframa?.value ||
          entity.inscricaoSuframa ||
          entity.suframa?.value ||
          entity.suframa;

        // Determine IE indicator based on entity data
        // Logic: Has CNPJ (company) + has IE = Contributor (1)
        //        Has CNPJ (company) + no IE = Non-contributor (9)
        //        Person (CPF only) = Non-contributor (9)
        //        Explicitly exempt = Exempt (2)
        let indIEDestValue = RECIPIENT_IE_INDICATOR.NON_CONTRIBUTOR; // Default for persons
        if (entityCnpj) {
          // Company
          if (entity.ieExempt || entity.ieIsento) {
            indIEDestValue = RECIPIENT_IE_INDICATOR.EXEMPT;
          } else if (entityIe) {
            indIEDestValue = RECIPIENT_IE_INDICATOR.CONTRIBUTOR;
          } else {
            indIEDestValue = RECIPIENT_IE_INDICATOR.NON_CONTRIBUTOR;
          }
        }

        // Set entity data in form
        form.setFieldsValue({
          [nameId]: entity._id,
          [name]: {
            entityId: entity._id,
            cpf: entityCpf,
            cnpj: entityCnpj,
            cpfCnpj: cpfCnpjValue, // Pre-fill for person, clear for others
            name: entity.name,
            fullName: entity.fullName || entity.razaoSocial,
            email: emailValue,
            phone: phoneValue,
            ie: entityIe || null, // null (not undefined) preserves AntD form binding so user can type
            indIEDest: indIEDestValue,
            inscricaoSuframa: entitySuframa || null,
            address: (() => {
                // getAddressInfo handles both entity.address (flat) and entity.addresses[] (array)
                const ea = getAddressInfo(entity);
                if (!ea) return undefined;
                return {
                  // Schema field names (persisted to DB via invoice addressSchema)
                  address1: ea.address1 || ea.street,
                  address2: ea.address2 || ea.complement,
                  number: ea.number,
                  neighborhood: ea.neighborhood || ea.district,
                  city: ea.city,
                  cityCode: ea.cityCode,
                  region: ea.region || ea.state,
                  zip: ea.zip || ea.zipCode,
                  countryCode: 'BR',
                  // Frontend aliases (for display fields and normalizeRecipientSnapshot)
                  street: ea.address1 || ea.street,
                  district: ea.neighborhood || ea.district,
                  state: ea.region || ea.state,
                  uf: ea.region || ea.state,
                  zipCode: ea.zip || ea.zipCode,
                };
              })(),
          },
        });
      } else if (!entity && form) {
        // Clear entity data when selection is cleared
        form.setFieldsValue({
          [nameId]: undefined,
          [name]: undefined,
        });
      }
    },
    [form]
  );

  // Handle entity saved from modal (new or edit)
  const handleEntitySaved = useCallback(
    savedEntity => {
      if (savedEntity) {
        // Select the saved entity
        handleEntitySelect(savedEntity);
      }
    },
    [handleEntitySelect]
  );

  // Open modal for new contact/recipient
  // Uses entityProfilesRef to avoid recreating this callback when parent passes a new array reference
  const handleNewRecipient = useCallback(() => {
    openModal(EditEntityModal, {
      entityProfile: entityProfilesRef.current?.[0] || 'customer',
      onConfirm: handleEntitySaved,
      requiredFields: ['entityProfile', 'uniqueKey', 'name', 'fullName'],
    });
  }, [handleEntitySaved]);

  // Open modal for editing selected recipient
  const handleEditRecipient = useCallback(() => {
    if (!selectedRecipient?._id) return;

    openModal(EditEntityModal, {
      entityId: selectedRecipient._id,
      entityProfile: selectedRecipient.entityProfile || 'recipient',
      onConfirm: handleEntitySaved,
      requiredFields: ['entityProfile', 'uniqueKey', 'name', 'fullName'],
    });
  }, [selectedRecipient, handleEntitySaved]);

  // Handle contact person selection (PJ sub-select)
  const handleContactSelect = useCallback(
    entity => {
      setSelectedContact(entity);
      if (form) {
        form.setFieldsValue({ [contactNameId]: entity?._id || undefined });
      }
    },
    [form, contactNameId]
  );

  // Notify parent about actions (for rendering buttons in Collapse header)
  useEffect(() => {
    onActionsChange?.({
      selectedRecipient,
      isGenericEntity,
      isSystemGenericEntity,
      handleNewRecipient,
      handleEditRecipient,
    });
  }, [
    selectedRecipient,
    isGenericEntity,
    isSystemGenericEntity,
    handleNewRecipient,
    handleEditRecipient,
    onActionsChange,
  ]);

  // -----------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // -----------------------------------------------------------------------------

  // Determine if selected entity is a company (PJ) - used for contact sub-select
  const isSelectedCompany =
    (selectedRecipient || data?.[nameEntity])?.entityType === 'company';

  // Show contact sub-select when: contactName prop provided + company selected + not authorized
  const showContactSelect = Boolean(contactName && isSelectedCompany && !isAuthorized);

  // Filter function for contact sub-select: only show contacts linked to the selected company
  const contactFilterFn = useCallback(
    entity => {
      const companyId = selectedRecipient?._id || data?.[nameId];
      if (!companyId) return true;
      return entity.linkedEntityId === companyId;
    },
    [selectedRecipient?._id, data?.[nameId]]
  );

  // Display selected entity info
  const recipientInfo = useMemo(() => {
    // Priority: selectedRecipient > entity > display > snapshot
    const recipient =
      selectedRecipient || data?.[nameEntity] || data?.[nameDisplay] || data?.[name];
    if (!recipient) return null;

    // Check for generic entities (finalConsumer, genericPerson, genericCompany, or no document)
    // Prioritize selectedRecipient if user just selected something
    const entityWithFlags = selectedRecipient || data?.[nameEntity] || recipient;

    // Check for explicit flags
    const hasGenericFlags =
      entityWithFlags?.finalConsumer ||
      entityWithFlags?.genericPerson ||
      entityWithFlags?.genericCompany;

    // Check if matches system generic entities
    const matchesSystemGeneric =
      (storedGenericPerson?._id && recipient?._id === storedGenericPerson._id) ||
      (storedGenericCompany?._id && recipient?._id === storedGenericCompany._id);

    // Check if entity has no document (uniqueKey is null/empty = no CPF/CNPJ)
    // This catches migrated "CONSUMIDOR" entities that don't have flags
    // Also check documents array (power-search returns CPF/CNPJ there)
    const hasUniqueKey = !!entityWithFlags?.uniqueKey;
    const hasDocumentInArray = entityWithFlags?.documents?.some(
      d => d.documentType === 'cpf' || d.documentType === 'cnpj'
    );
    const hasCpfOrCnpjDirect =
      !!entityWithFlags?.cpf?.value ||
      !!entityWithFlags?.cpf ||
      !!entityWithFlags?.cnpj?.value ||
      !!entityWithFlags?.cnpj;
    const hasNoDocument = !hasUniqueKey && !hasDocumentInArray && !hasCpfOrCnpjDirect;

    const isGenericRecipient = hasGenericFlags || matchesSystemGeneric || hasNoDocument;

    // Logic table:
    // A) PROCESSED + CONSUMER → LIMITED Descriptions (name + document only)
    // B) PROCESSED + NOT CONSUMER → ALL Descriptions
    // C) NOT PROCESSED + CONSUMER → NO Descriptions (return null)
    // D) NOT PROCESSED + NOT CONSUMER → ALL Descriptions

    // Case C: NOT PROCESSED + CONSUMER → No descriptions
    if (!isAuthorized && isGenericRecipient) {
      return null;
    }

    // Determine entity type for document label
    const isCompany =
      recipient.entityType === 'company' ||
      !!recipient.cnpj ||
      recipient.documents?.some(d => d.documentType === 'cnpj');

    // Determine country
    const countryCode = recipient.countryCode || recipient.address?.countryCode || 'BR';
    const isBrazil = countryCode === 'BR';

    // Build first row content based on country and entity type
    const firstRowContent = buildFirstRowContent(recipient, isBrazil, isCompany);

    // Case A: PROCESSED + CONSUMER → Limited descriptions (name + document on same line on desktop)
    if (isAuthorized && isGenericRecipient) {
      // Desktop: same line (span=1), Mobile: separate lines (span=2)
      const itemSpan = isMobile ? 2 : 1;

      // Get CPF/CNPJ from snapshot (cpfCnpj) or entity (cpf.value)
      const consumerDocument =
        data?.[name]?.cpfCnpj ||
        data?.[nameEntity]?.cpf?.value ||
        data?.[nameEntity]?.cpf ||
        recipient?.cpf?.value ||
        recipient?.cpf;

      // Format document for display
      const formattedDocument = consumerDocument
        ? formatDocumentDisplay(consumerDocument, false)
        : '-';

      return (
        <Descriptions
          column={isMobile ? 1 : 2}
          layout={isMobile ? 'vertical' : 'horizontal'}
          size="small"
          bordered
          styles={{ label: { width: isMobile ? undefined : 120, fontWeight: 500, textAlign: 'left' } }}
        >
          {/* Name and Document - same line on desktop, separate on mobile */}
          <Descriptions.Item label={translateX('name')} span={itemSpan}>
            {recipient.fullName || recipient.razaoSocial || recipient.name || '-'}
          </Descriptions.Item>

          <Descriptions.Item label="CPF" span={itemSpan}>
            {formattedDocument}
          </Descriptions.Item>
        </Descriptions>
      );
    }

    // Cases B and D: Full descriptions
    // Extract values using helper functions
    const phone = getPhoneValue(recipient);
    const email = getEmailValue(recipient);
    const address = getAddressInfo(recipient);

    // Build address string - handle undefined values properly
    const addressInfo = buildAddressString(address);

    // Build city string - handle undefined values properly
    const cityInfo = buildCityString(address);

    // Contact person info (when company selected and contact exists)
    const contact = isCompany ? selectedContact : null;
    const contactName_ = contact?.name || contact?.fullName || '';
    const contactPhone = contact ? parsePhoneToDisplay(getPhoneValue(contact)) : '';
    const contactEmail = contact ? getEmailValue(contact) : '';
    const contactPhoneEmailParts = [contactPhone, contactEmail].filter(Boolean);

    // Check if paired row values are both empty - hide such rows
    const hasDocumentRow =
      firstRowContent.value !== '-' ||
      (firstRowContent.secondaryLabel && firstRowContent.secondaryValue !== '-');
    const hasAddressCityRow = addressInfo !== '-' || cityInfo !== '-';
    const hasEmailPhoneRow = email || phone;

    return (
      <Descriptions
        column={isMobile ? 1 : 2}
        layout={isMobile ? 'vertical' : 'horizontal'}
        size="small"
        bordered
        style={{ marginTop: !isAuthorized ? 16 : 0 }}
        styles={{ label: { width: isMobile ? undefined : 120, fontWeight: 500, textAlign: 'left' } }}
      >
        {isCompany ? (
          <Descriptions.Item label={translateX('company_name')} span={2}>
            {recipient.fullName || recipient.razaoSocial || recipient.name || '-'}
          </Descriptions.Item>
        ) : null}

        {hasDocumentRow && (
          <>
            <Descriptions.Item label={firstRowContent.label} span={firstRowContent.span}>
              {firstRowContent.value}
            </Descriptions.Item>

            {firstRowContent.secondaryLabel && (
              <Descriptions.Item label={firstRowContent.secondaryLabel}>
                {firstRowContent.secondaryValue}
              </Descriptions.Item>
            )}
          </>
        )}

        {hasAddressCityRow && (
          <>
            <Descriptions.Item label={translateX('address')}>{addressInfo}</Descriptions.Item>
            <Descriptions.Item label={translateX('city')}>{cityInfo}</Descriptions.Item>
          </>
        )}

        {hasEmailPhoneRow && (
          <>
            <Descriptions.Item label={translateX('email')}>{email || '-'}</Descriptions.Item>
            <Descriptions.Item label={translateX('phone')}>
              {parsePhoneToDisplay(phone) || '-'}
            </Descriptions.Item>
          </>
        )}

        {/* Contact person row - shown when PJ entity + contact selected */}
        {contact && (
          <>
            <Descriptions.Item label={translateX('contact')}>
              {contactName_}
            </Descriptions.Item>
            <Descriptions.Item label={translateX('phone') + '/' + translateX('email')}>
              {contactPhoneEmailParts.length > 0
                ? contactPhoneEmailParts.map((part, i) => (
                    <span key={i}>
                      {i > 0 && <Blob />}
                      {part}
                    </span>
                  ))
                : '-'}
            </Descriptions.Item>
          </>
        )}
      </Descriptions>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedRecipient,
    selectedContact,
    data?.[nameDisplay],
    data?.[nameEntity],
    data?.[name],
    data?.[name]?.name,
    storedLanguage,
    storedGenericPerson?._id,
    storedGenericCompany?._id,
    isAuthorized,
    isMobile,
  ]);

  // -----------------------------------------------------------------------------
  // 8. UI
  // -----------------------------------------------------------------------------
  return (
    <>
      {/* Show select only when not authorized */}
      {!isAuthorized && (
        <Row gutter={[16, 16]}>
          {/* Entity select - shrinks when contact sub-select or CPF field is shown on the right */}
          <Col
            xs={24}
            sm={showContactSelect ? 14 : isRecipient && isGenericEntity ? 16 : 24}
          >
            <SimpleSelectAsyncEntities
              name={nameId}
              label={label || 'search_recipient'}
              placeholder="search_recipient_placeholder"
              required
              disabled={isLocked}
              onEntitySelect={handleEntitySelect}
              entityProfiles={entityProfiles || ['customer', 'provider', 'company']}
              selectedEntity={selectedRecipient}
            />
          </Col>

          {/* Contact person sub-select - shown when a company (PJ) is selected */}
          {showContactSelect && (
            <Col xs={24} sm={10}>
              <SimpleSelectAsyncEntities
                name={contactNameId}
                label="contact_person"
                placeholder="search_entity"
                disabled={isLocked}
                onEntitySelect={handleContactSelect}
                entityProfiles={CONTACT_PROFILES}
                filterFn={contactFilterFn}
                selectedEntity={selectedContact}
              />
            </Col>
          )}

          {/* Show CPF field when generic entity is selected - saves to cpfCnpj (invoice-mode only) */}
          {isRecipient && isGenericEntity && (
            <Col xs={24} sm={8}>
              <SimpleCpf
                name={[name, 'cpfCnpj']}
                label={translateX('cpf')}
                placeholder={translateX('cpf_optional')}
              />
            </Col>
          )}
        </Row>
      )}

      {/* NFe B2B fields - invoice-mode only, shown when NFe and recipient selected, not authorized */}
      {isRecipient && isNfe && selectedRecipient && !isAuthorized && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <SimpleSelect
                name={[name, 'indIEDest']}
                label="ie_indicator"
                placeholder="select_ie_indicator"
                disabled={isLocked}
                required
                options={[
                  { value: RECIPIENT_IE_INDICATOR.CONTRIBUTOR, label: translateX('ie_contributor') },
                  { value: RECIPIENT_IE_INDICATOR.EXEMPT, label: translateX('ie_exempt') },
                  { value: RECIPIENT_IE_INDICATOR.NON_CONTRIBUTOR, label: translateX('ie_non_contributor') },
                ]}
              />
            </Col>
            <Col xs={24} md={8}>
              <SimpleInput
                name={[name, 'ie']}
                label="state_tax_id"
                placeholder="ie_placeholder"
                disabled={isLocked || indIEDest === RECIPIENT_IE_INDICATOR.EXEMPT}
                required={isContributor}
                maxLength={14}
              />
            </Col>
            <Col xs={24} md={8}>
              <SimpleInput
                name={[name, 'inscricaoSuframa']}
                label="suframa"
                placeholder="suframa_placeholder"
                tooltip="suframa_tooltip"
                disabled={isLocked}
                maxLength={9}
              />
            </Col>
          </Row>
        </>
      )}

      {/* Hidden fields to store entity data */}
      <SimpleInput name={[name, 'entityId']} hidden />
      <SimpleInput name={[name, 'name']} hidden />
      <SimpleInput name={[name, 'fullName']} hidden />
      <SimpleInput name={[name, 'email']} hidden />
      <SimpleInput name={[name, 'phone']} hidden />
      {/* Hidden field for contact person ID (when contactName prop is provided) */}
      {contactNameId && <SimpleInput name={contactNameId} hidden />}
      {/* Invoice-specific hidden fields (CPF, CNPJ, fiscal fields) */}
      {isRecipient && (
        <>
          {/* CPF hidden field for regular entities */}
          <SimpleInput name={[name, 'cpf']} hidden />
          {/* cpfCnpj hidden field: show when authorized OR not generic (visible SimpleCpf shown otherwise) */}
          {(isAuthorized || !isGenericEntity) && (
            <SimpleInput name={[name, 'cpfCnpj']} hidden />
          )}
          <SimpleInput name={[name, 'cnpj']} hidden />
          {/* IE hidden field: only when NOT NFe (NFe shows visible field) or when authorized */}
          {(!isNfe || isAuthorized) && <SimpleInput name={[name, 'ie']} hidden />}
          {/* indIEDest hidden field: only when NFe B2B visible block is NOT rendered */}
          {(!isNfe || !selectedRecipient || isAuthorized) && (
            <SimpleInput name={[name, 'indIEDest']} hidden />
          )}
          {/* inscricaoSuframa hidden field: only when NFe B2B visible block is NOT rendered */}
          {(!isNfe || !selectedRecipient || isAuthorized) && (
            <SimpleInput name={[name, 'inscricaoSuframa']} hidden />
          )}
        </>
      )}

      {/* Recipient info display */}
      {recipientInfo}
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default RecipientCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Normalize recipient snapshot from backend field names to frontend form field names.
 * When a reversal/complementary invoice is created by the backend, the recipient snapshot
 * may use entity schema field names instead of the frontend form field names.
 *
 * Backend schema  →  Frontend form
 * stateRegistration       → ie
 * stateRegistrationIndicator → indIEDest
 * cpfCnpj (14 digits)    → cnpj
 * cpfCnpj (11 digits)    → cpf
 *
 * Only sets a normalized field if the corresponding form field is missing/empty.
 * This prevents overwriting values that were already properly saved.
 */
function normalizeRecipientSnapshot(snap) {
  if (!snap) return {};
  const normalized = {};

  // ie: map stateRegistration → ie when ie is not already set
  if (!snap.ie && snap.stateRegistration) {
    normalized.ie = snap.stateRegistration;
  }

  // indIEDest: map stateRegistrationIndicator → indIEDest when indIEDest is not already set
  if ((snap.indIEDest === undefined || snap.indIEDest === null) && snap.stateRegistrationIndicator !== undefined) {
    normalized.indIEDest = snap.stateRegistrationIndicator;
  }

  // cnpj/cpf: resolve from cpfCnpj when individual fields are not already set
  if (!snap.cnpj && !snap.cpf && snap.cpfCnpj) {
    const digits = String(snap.cpfCnpj).replace(/\D/g, '');
    if (digits.length === 14) {
      normalized.cnpj = digits;
    } else if (digits.length === 11) {
      normalized.cpf = digits;
    }
  }

  // fullName: map razaoSocial → fullName when fullName is not already set
  if (!snap.fullName && snap.razaoSocial) {
    normalized.fullName = snap.razaoSocial;
  }

  // address: normalize address field names when address.street is missing but address1 exists
  if (snap.address && !snap.address.street && snap.address.address1) {
    normalized.address = {
      ...snap.address,
      street: snap.address.address1,
      district: snap.address.neighborhood || snap.address.district,
      state: snap.address.region || snap.address.state,
      uf: snap.address.region || snap.address.state,
      zipCode: snap.address.zip || snap.address.zipCode,
    };
  }

  return normalized;
}

/**
 * Extract phone value from entity
 * Handles: object with value, array of objects, or string
 */
function getPhoneValue(entity) {
  if (!entity) return undefined;

  const phone = entity.phone;

  // If phone is an object with value property (power-search response)
  if (phone && typeof phone === 'object' && !Array.isArray(phone)) {
    return phone.displayValue || phone.value || undefined;
  }

  // If phone is an array of objects, get the primary or first one
  if (Array.isArray(phone)) {
    const primaryPhone = phone.find(p => p.isPrimary) || phone[0];
    return primaryPhone?.displayValue || primaryPhone?.value || undefined;
  }

  // If phone is a string, return it
  if (typeof phone === 'string') {
    return phone;
  }

  // Try contacts array as fallback
  if (Array.isArray(entity.contacts)) {
    const phoneContact =
      entity.contacts.find(c => c.contactType === 'phone' && c.isPrimary) ||
      entity.contacts.find(c => c.contactType === 'phone');
    return phoneContact?.displayValue || phoneContact?.value || undefined;
  }

  return undefined;
}

/**
 * Extract email value from entity
 * Handles: object with value, array of objects, or string
 */
function getEmailValue(entity) {
  if (!entity) return undefined;

  const email = entity.email;

  // If email is an object with value property (power-search response)
  if (email && typeof email === 'object' && !Array.isArray(email)) {
    return email.value || undefined;
  }

  // If email is an array of objects, get the primary or first one
  if (Array.isArray(email)) {
    const primaryEmail = email.find(e => e.isPrimary) || email[0];
    return primaryEmail?.value || undefined;
  }

  // If email is a string, return it
  if (typeof email === 'string') {
    return email;
  }

  // Try contacts array as fallback
  if (Array.isArray(entity.contacts)) {
    const emailContact =
      entity.contacts.find(c => c.contactType === 'email' && c.isPrimary) ||
      entity.contacts.find(c => c.contactType === 'email');
    return emailContact?.value || undefined;
  }

  return undefined;
}

/**
 * Extract document value (CPF/CNPJ) from entity
 * Handles: object with value, or string
 */
function getDocumentValue(entity) {
  if (!entity) return undefined;

  // Try CNPJ first
  const cnpj = entity.cnpj;
  if (cnpj) {
    if (typeof cnpj === 'object' && cnpj.value) return cnpj.value;
    if (typeof cnpj === 'string') return cnpj;
  }

  // Try CPF
  const cpf = entity.cpf;
  if (cpf) {
    if (typeof cpf === 'object' && cpf.value) return cpf.value;
    if (typeof cpf === 'string') return cpf;
  }

  // Try documents array
  if (Array.isArray(entity.documents)) {
    const cnpjDoc = entity.documents.find(d => d.documentType === 'cnpj');
    if (cnpjDoc?.value) return cnpjDoc.value;

    const cpfDoc = entity.documents.find(d => d.documentType === 'cpf');
    if (cpfDoc?.value) return cpfDoc.value;
  }

  return undefined;
}

/**
 * Extract IE (Inscrição Estadual) from entity
 */
function getIeValue(entity) {
  if (!entity) return undefined;

  // Get state for IE formatting
  const entityAddr = getAddressInfo(entity);
  const uf = entityAddr?.state || entityAddr?.region || entity.fiscalData?.state;

  // Direct IE field
  if (entity.ie) {
    if (typeof entity.ie === 'object' && entity.ie.value) {
      return parseIeToDisplay(entity.ie.value, uf);
    }
    if (typeof entity.ie === 'string') {
      return parseIeToDisplay(entity.ie, uf);
    }
  }

  // Try documents array
  if (Array.isArray(entity.documents)) {
    const ieDoc = entity.documents.find(d => d.documentType === 'ie');
    if (ieDoc?.value) return parseIeToDisplay(ieDoc.value, uf);
  }

  // Try fiscalData
  if (entity.fiscalData?.stateRegistration) {
    return parseIeToDisplay(entity.fiscalData.stateRegistration, uf);
  }

  return undefined;
}

/**
 * Extract primary address from entity
 */
function getAddressInfo(entity) {
  if (!entity) return undefined;

  // Direct address field (power-search response)
  if (entity.address && typeof entity.address === 'object') {
    return entity.address;
  }

  // Try addresses array
  if (Array.isArray(entity.addresses) && entity.addresses.length > 0) {
    const primaryAddress = entity.addresses.find(a => a.isPrimary) || entity.addresses[0];
    return primaryAddress;
  }

  return undefined;
}

/**
 * Format document (CPF/CNPJ) for display
 */
function formatDocumentDisplay(value, isCompany) {
  if (!value) return '-';

  const digits = String(value).replace(/\D/g, '');

  if (isCompany && digits.length === 14) {
    // CNPJ: XX.XXX.XXX/XXXX-XX
    return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }

  if (!isCompany && digits.length === 11) {
    // CPF: XXX.XXX.XXX-XX
    return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }

  return value;
}

/**
 * Extract RG (Identity Document) from entity - for person entities
 */
function getRgValue(entity) {
  if (!entity) return undefined;

  // Direct RG field
  if (entity.rg) {
    if (typeof entity.rg === 'object' && entity.rg.value) {
      return entity.rg.value;
    }
    if (typeof entity.rg === 'string') {
      return entity.rg;
    }
  }

  // Try documents array
  if (Array.isArray(entity.documents)) {
    const rgDoc = entity.documents.find(d => d.documentType === 'rg');
    if (rgDoc?.value) return rgDoc.value;
  }

  return undefined;
}

/**
 * Build address string with proper handling of undefined values
 */
function buildAddressString(address) {
  if (!address) return '-';

  const parts = [];

  // Street/address1
  if (address.address1 || address.street) {
    parts.push(address.address1 || address.street);
  }

  // Number
  if (address.number) {
    parts.push(address.number);
  }

  // Complement/address2
  if (address.address2 || address.complement) {
    parts.push(address.address2 || address.complement);
  }

  // If no parts, return dash
  if (parts.length === 0) return '-';

  // Join with comma for street, then space for number and complement
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]}, ${parts[1]}`;
  return `${parts[0]}, ${parts[1]} ${parts[2]}`;
}

/**
 * Build city string with proper handling of undefined values
 */
function buildCityString(address) {
  if (!address) return '-';

  const city = address.city;
  const state = address.region || address.state;

  if (!city && !state) return '-';
  if (!city) return state;
  if (!state) return city;

  return `${city} - ${state}`;
}

/**
 * Build first row content based on country and entity type
 * - BR + COMPANY: CNPJ | IE
 * - BR + PERSON: CPF (span 2, no secondary)
 * - Other countries: foreign_identifier (span 2, no secondary)
 */
function buildFirstRowContent(recipient, isBrazil, isCompany) {
  if (isBrazil) {
    if (isCompany) {
      // BR + COMPANY: CNPJ | IE
      const cnpj = getDocumentValue(recipient);
      const ie = getIeValue(recipient);
      return {
        label: 'CNPJ',
        value: cnpj ? formatDocumentDisplay(cnpj, true) : '-',
        span: 1,
        secondaryLabel: translateX('state_tax_id'),
        secondaryValue: ie || translateX('exempt'),
      };
    } else {
      // BR + PERSON: CPF only (span 2)
      const cpf = getDocumentValue(recipient);
      return {
        label: 'CPF',
        value: cpf ? formatDocumentDisplay(cpf, false) : '-',
        span: 2,
        secondaryLabel: null,
        secondaryValue: null,
      };
    }
  } else {
    // Other countries: foreign_identifier (span 2)
    const foreignId = getForeignIdentifierValue(recipient);
    return {
      label: translateX('foreign_identifier'),
      value: foreignId || '-',
      span: 2,
      secondaryLabel: null,
      secondaryValue: null,
    };
  }
}

/**
 * Extract foreign identifier from entity (for non-BR countries)
 */
function getForeignIdentifierValue(entity) {
  if (!entity) return undefined;

  // Direct foreignIdentifier field
  if (entity.foreignIdentifier) {
    if (typeof entity.foreignIdentifier === 'object' && entity.foreignIdentifier.value) {
      return entity.foreignIdentifier.value;
    }
    if (typeof entity.foreignIdentifier === 'string') {
      return entity.foreignIdentifier;
    }
  }

  // Try documents array
  if (Array.isArray(entity.documents)) {
    const foreignDoc = entity.documents.find(d => d.documentType === 'foreignIdentifier');
    if (foreignDoc?.value) return foreignDoc.value;
  }

  return undefined;
}
